import { cn } from '@/lib/utils';
import { thumb, track, trackFill } from '../../tokens';
import { displayRange, fromDisplay, toDisplay } from '../format';
import { NumberControl } from './NumberControl';
import type { ControlProps } from './types';

/** Where a value sits on a track, as a percentage of its length. */
export const alongTrack = (v: number, min: number, max: number): number =>
  max > min ? Math.min(100, Math.max(0, ((v - min) / (max - min)) * 100)) : 0;

/** A number with a range: a slider across the control column, and the number beside it to type exactly. */
export function SliderControl(props: ControlProps) {
  const { id, setting, value, onChange, disabled } = props;
  const { min = 0, max = 1, step } = displayRange(setting);
  const shown = typeof value === 'number' && Number.isFinite(value) ? toDisplay(setting, value) : min;
  const at = Math.min(max, Math.max(min, shown));
  return (
    <div className="flex w-full items-center gap-3">
      <div className="relative flex h-5 min-w-0 flex-1 items-center">
        <div className={cn('absolute inset-x-0 h-1.5 rounded-full', track)} aria-hidden />
        <div className={cn('absolute left-0 h-1.5 rounded-full', trackFill)} style={{ width: `${alongTrack(at, min, max)}%` }} aria-hidden />
        <input
          type="range"
          aria-label={`${setting.ui.title} (slider)`}
          aria-controls={id}
          min={min}
          max={max}
          step={step}
          value={at}
          disabled={disabled}
          onChange={(e) => onChange(fromDisplay(setting, Number(e.target.value)))}
          className={cn('absolute inset-0 cursor-pointer', thumb)}
        />
      </div>
      <NumberControl {...props} compact className="shrink-0" />
    </div>
  );
}
